import React from 'react'
import { Form } from 'antd'
import { match } from 'ts-pattern'
import dynamic from 'next/dynamic'
import { useLingui } from '@lingui/react/macro'

import { useAiMusicGeneratorStoreFromZustand } from '../../_store/createStore'
import { useMusicFormStore } from '../../_store/useMusicFormStore'

import { normalizeMusicGeneratorFormValues } from './_hooks/normalizeMusicGeneratorFormValues'
import { useFormGenerate } from './_hooks/useFormGenerate'
import { GeneratorFormProvider } from './_store'
import SmartForm from './Forms/SmartForm'
import CustomForm from './Forms/CustomForm'

import type { PromptMode } from '@loc/server/interface/task'
import type { FormInstance } from 'antd'
import type { Promisable } from 'type-fest'
import type { MusicGeneratorFormValuesNew } from '../../_types/form'

import HeaderTabs from '@/pages/tad.ai/_components/HeaderTabs'
import ScrollArea from '@/pages/tad.ai/_components/ScrollArea'
import { useScreen } from '@/hooks/screen'


import { useRef, useEffect } from 'react';
import { cls } from 'tagged-classnames-free';
const EditAudioModalDynamic = dynamic(
  () => import('./_blocks/EditAudioModal'),
  {
    ssr: false,
  },
)
const YoutubeLinkModalDynamic = dynamic(
  () => import('./_blocks/YoutubeLinkModal'),
  {
    ssr: false,
  },
)
const HistoryModalDynamic = dynamic(() => import('./_blocks/HistoryModal'), {
  ssr: false,
})

const AiIdeasModalDynamic = dynamic(() => import('./_blocks/AiIdeasModal'), {
  ssr: false,
})
const MoreIdeasModalDynamic = dynamic(
  () => import('./_blocks/MoreIdeasModal'),
  {
    ssr: false,
  },
)

export interface GeneratorFormProps {
  className?: string
  onCreated?: () => Promisable<void>
}

const GeneratorForm: React.FC<GeneratorFormProps> = (props) => {
  const { className, onCreated } = props
  const { t } = useLingui()
  const isXl = useScreen('xl')

  // ─── Store 状态订阅 ───
  const formValues = useMusicFormStore((s) => s.form)
  const hasHydrated = useMusicFormStore((s) => s._hasHydrated)
  const setFormValues = useMusicFormStore((s) => s.setFormValues)

  // store 创建时已同步注入 forcedFormDefaults，hydration 前 formValues 就是正确的默认值
  // Music 页面 = MUSIC_FORM_DEFAULTS，Rap 页面 = { ...MUSIC_FORM_DEFAULTS, ...RAP_FORCED_DEFAULTS }
  const displayValues = formValues

  const initializedFormRef = useRef(false)
  const [form] = Form.useForm<MusicGeneratorFormValuesNew>()

  const { handleGenerate, generateMusicLoading } = useFormGenerate({
    onSuccess: onCreated,
  })

  const promptTabsMode = Form.useWatch(['promptTabsMode'], form)

  const { updateStates } = useAiMusicGeneratorStoreFromZustand()

  useEffect(() => {
    if (!initializedFormRef.current && hasHydrated) {
      initializedFormRef.current = true
      // 更新 AI 生成 store
      updateStates({
        finalPrompt: formValues.prompt || '',
        finalPromptSelectMode: formValues.promptSelectMode as PromptMode,
        finalTaskType: formValues.taskType || 'Song',
        finalImageUrl: formValues.imageUrl || '',
      })
      // 用 store 中的值填充 antd Form
      form.setFieldsValue(normalizeMusicGeneratorFormValues(formValues))
    }
  }, [form, hasHydrated, formValues, updateStates])

  return (
    <GeneratorFormProvider>
      <div
        className={cls`bg-f-other-1 scrollbar z-0 mb-6 flex-col overflow-x-hidden rounded-2xl xl:w-[680px] ${className}`}
      >
        <Form<MusicGeneratorFormValuesNew>
          layout='vertical'
          preserve
          className={cls`
            scrollbar-none mx-4 flex
            h-full flex-col rounded-2xl
            md:mx-8
            xl:max-w-none
            xl:overflow-auto
            [&_[class*="form-item-label"]>label]:text-base
            [&_[class*="form-item-label"]>label]:font-semibold
          `}
          form={form}
          initialValues={normalizeMusicGeneratorFormValues(displayValues)}
          onValuesChange={(_, values) => {
            // imgurl出错的时候还是会跑保存本地的逻辑
            const newValues = {
              ...values,
              imageUrl: values.imageUrl?.startsWith('http')
                ? values.imageUrl
                : undefined,
            }
            setFormValues(newValues)
          }}
          onFinish={async (values) => {
            try {
              await handleGenerate(values)
            } catch (err) {
              // 使用 trpc 全局默认异常处理即可
            }
          }}
          scrollToFirstError
        >
          {/** tabs*/}
          <Form.Item noStyle name='promptTabsMode'>
            <HeaderTabs
              activeKey={
                promptTabsMode || displayValues.promptTabsMode || 'smart'
              }
              items={[
                { label: t`Smart`, key: 'smart' },
                { label: t`Custom`, key: 'custom' },
              ]}
              onChange={(key) => {
                form.setFieldValue('promptTabsMode', key)
                setFormValues({
                  promptTabsMode: key as 'smart' | 'custom',
                })
              }}
            />
          </Form.Item>
          {/** form area */}
          {match(promptTabsMode)
            .with('smart', () => {
              return (
                <ScrollArea
                  disabled={!isXl}
                  className={cls`bg-f-bg-elevated flex-1 overflow-hidden rounded-2xl ${promptTabsMode === 'smart' ? 'rounded-tl-none' : ''}`}
                >
                  <SmartForm
                    form={form as FormInstance<MusicGeneratorFormValuesNew>}
                    className={cls`pt-5`}
                    generateMusicLoading={generateMusicLoading}
                  />
                </ScrollArea>
              )
            })
            .with('custom', () => {
              return (
                <ScrollArea
                  disabled={!isXl}
                  className={cls`bg-f-bg-elevated flex-1 overflow-hidden rounded-2xl`}
                >
                  <CustomForm
                    form={form as FormInstance<MusicGeneratorFormValuesNew>}
                    className={cls`pt-5`}
                    generateMusicLoading={generateMusicLoading}
                  />
                </ScrollArea>
              )
            })
            .otherwise(() => null)}
          {/** version2 end */}
        </Form>
        <EditAudioModalDynamic />
        <YoutubeLinkModalDynamic />
        <HistoryModalDynamic />
        <AiIdeasModalDynamic
          form={form as FormInstance<MusicGeneratorFormValuesNew>}
        />
        <MoreIdeasModalDynamic
          form={form as FormInstance<MusicGeneratorFormValuesNew>}
        />
      </div>
    </GeneratorFormProvider>
  )
}

export default GeneratorForm
